/*
 * Linked Products Group
 *
 * Both modes: appends the host variant picker title (template[data-variant-picker-title]) above the picker.
 *
 * Host PDP mode (data-host-pdp):
 * Loads a product of the group inside the current (host) product page instead of navigating to it.
 * The linked product's own page is fetched (it may use a different template) and its gallery, price,
 * SKU, add to cart, prescription button, sticky add to cart and template sections replace the host ones.
 * Host PDP sections and elements are identified with data-host-pdp-section-handle (see assignHostPdpHandles),
 * applied the same way to the host page and to the fetched linked product page.
 * Every change is recorded so the host content can be restored when a host variant, or the host
 * product in the group, is clicked.
 */
(() => {
  if (customElements.get('linked-products-group')) return;

  // Elements of the main product section swapped 1:1 with the linked product's, by handle.
  // A value is a selector, or { selector, index, linkedIndex, scope } when the page has several matches.
  // index is a position or 'last' (linkedIndex overrides it on a fetched linked product page);
  // scope 'document' searches the whole page instead of the main product section.
  const HOST_PDP_ELEMENTS = {
    'product-media': ':scope > .product-media',
    // App block wrapper, e.g. id="shopify-block-AYkZOb1RlQzFiTjQ0c__section_store_block_benefits_list_NENgRd".
    // The page renders more than one: the host's own list is the last, the linked page's is the second.
    'benefits-list': {
      selector: '[id*="section_store_block_benefits_list"]',
      index: 'last',
      linkedIndex: 1,
      scope: 'document',
    },
    // Safety standards badges from the "Safety Callouts" custom liquid block (sunglasses only).
    'safety-standards': '.product-info .wx-std',
    'rx-button': '.product-info .magento-rx',
    'customize-cta': '.product-info .product-customize-cta',
    // Shop Pay installments widget. Its shopify-meta only prices the page's own product variants.
    'payment-terms': '.product-info shopify-payment-terms',
    'sticky-add-to-cart': ':scope > .sticky-add-to-cart-wrapper',
  };
  const RETURNS_CONFIDENCE_HANDLE = 'returns-confidence';

  // Template sections that always stay from the host PDP (not handled), identified by their content:
  // apps (app blocks), recently viewed products and related products.
  const HOST_PDP_SHARED_SECTIONS = ['.shopify-app-block', 'recently-viewed-products', 'product-recommendations'];

  // [data-variant-info] elements that are not copied from the linked product page:
  // the host variant picker, and the Style description, which this component fills itself.
  const HOST_ONLY_VARIANT_INFO = ['variant-picker', 'linked-products-style'];

  const RETURNS_CONFIDENCE_ITEM = /30.day\s+returns?/i;

  const pageCache = new Map();

  function fetchPage(url) {
    if (!pageCache.has(url)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load ${url} (${response.status})`);
          return response.text();
        })
        .catch((error) => {
          pageCache.delete(url);
          throw error;
        });
      pageCache.set(url, request);
    }
    return pageCache.get(url);
  }

  /**
   * Marks the PDP sections and elements that belong to a product with data-host-pdp-section-handle.
   * - Template sections (every section of the product template except the main product section and
   *   HOST_PDP_SHARED_SECTIONS) use their section key, e.g. collapsible_content_XfMD6V.
   *   Section groups (header, footer) are skipped.
   * - Main product elements use the HOST_PDP_ELEMENTS keys, plus the 30-day returns confidence icon.
   * @param {Document} root The host document or a fetched linked product page.
   * @param {boolean} [isLinked] True when root is a fetched linked product page.
   */
  function assignHostPdpHandles(root, isLinked = false) {
    const mainProduct = root.querySelector('.section-main-product');

    root.querySelectorAll('main > .shopify-section[id*="template--"]').forEach((section) => {
      if (section === mainProduct) return;
      if (HOST_PDP_SHARED_SECTIONS.some((selector) => section.querySelector(selector))) return;
      section.dataset.hostPdpSectionHandle = section.id.split('__').pop();
    });

    if (!mainProduct) return;

    Object.entries(HOST_PDP_ELEMENTS).forEach(([handle, config]) => {
      const { selector, index = 0, linkedIndex, scope } = typeof config === 'string' ? { selector: config } : config;
      const position = isLinked && linkedIndex !== undefined ? linkedIndex : index;
      const matches = (scope === 'document' ? root : mainProduct).querySelectorAll(selector);
      const element = position === 'last' ? matches[matches.length - 1] : matches[position];
      if (element) element.dataset.hostPdpSectionHandle = handle;
    });
    mainProduct.querySelectorAll('.confidence-item').forEach((item) => {
      if (RETURNS_CONFIDENCE_ITEM.test(item.textContent)) item.dataset.hostPdpSectionHandle = RETURNS_CONFIDENCE_HANDLE;
    });
  }

  function findByHandle(root, handle) {
    return root.querySelector(`[data-host-pdp-section-handle="${handle}"]`);
  }

  // Scripts inserted with the DOM API don't run; recreate them so section scripts initialise.
  function runInlineScripts(root) {
    root.querySelectorAll('script').forEach((original) => {
      if (original.type && !['text/javascript', 'module'].includes(original.type)) return;
      const script = document.createElement('script');
      Array.from(original.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
      script.textContent = original.textContent;
      original.replaceWith(script);
    });
  }

  /**
   * The theme's <media-carousel> reads this.section (a closest('.shopify-section') getter) in its
   * disconnectedCallback, which is null once the element is detached and throws. Pinning the value as an
   * own property shadows the getter, so galleries can be swapped in and out without that error.
   * @param {Element} root Element about to be detached, or just inserted.
   */
  function pinSectionReferences(root) {
    root.querySelectorAll('media-carousel').forEach((carousel) => {
      if (Object.hasOwn(carousel, 'section')) return;
      const section = carousel.closest('.shopify-section');
      if (section) Object.defineProperty(carousel, 'section', { value: section, configurable: true });
    });
  }

  function getAttributes(element) {
    return Array.from(element.attributes, ({ name, value }) => [name, value]);
  }

  function setAttributes(element, attributes) {
    Array.from(element.attributes).forEach(({ name }) => element.removeAttribute(name));
    attributes.forEach(([name, value]) => element.setAttribute(name, value));
  }

  class LinkedProductsGroup extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-main-product');
      // Quick add modal and non-product pages keep the plain links.
      if (!this.section || this.closest('#quick-add-modal')) return;

      this.appendVariantPickerTitle();

      // Links mode: thumbnails stay plain links.
      this.hostPdpEnabled = this.hasAttribute('data-host-pdp');
      if (!this.hostPdpEnabled) return;

      this.hostProductId = this.dataset.hostProductId;
      this.hostTitle = document.title;
      this.activeProductId = null;
      this.undoStack = [];
      this.requestId = 0;
      assignHostPdpHandles(document);

      this.onClick = this.onClick.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);
      this.onVariantPickerInteraction = this.onVariantPickerInteraction.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPrefetch);
      this.addEventListener('focusin', this.onPrefetch);
      // Capture phase so the host content is back before the theme's variant picker handles the change.
      this.section.addEventListener('click', this.onVariantPickerInteraction, true);
      this.section.addEventListener('change', this.onVariantPickerInteraction, true);
    }

    disconnectedCallback() {
      if (!this.hostPdpEnabled) return;
      this.section.removeEventListener('click', this.onVariantPickerInteraction, true);
      this.section.removeEventListener('change', this.onVariantPickerInteraction, true);
    }

    // Inserts the title before <variant-picker>, outside it, so the theme's picker re-render keeps it.
    appendVariantPickerTitle() {
      const template = this.querySelector('template[data-variant-picker-title]');
      const picker = this.section.querySelector('variant-picker');
      if (!template || !picker || picker.previousElementSibling?.hasAttribute('data-variant-picker-title')) return;

      const title = template.content.firstElementChild.cloneNode(true);
      title.setAttribute('data-variant-picker-title', '');
      picker.before(title);
    }

    get options() {
      return Array.from(this.querySelectorAll('[data-linked-product-option]'));
    }

    onClick(event) {
      const option = event.target.closest('[data-linked-product-option]');
      if (!option || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      if (option.dataset.productId === this.hostProductId) {
        this.showHost();
      } else {
        this.showLinkedProduct(option);
      }
    }

    onPrefetch(event) {
      const option = event.target.closest('[data-linked-product-option]');
      if (!option || option.dataset.productId === this.hostProductId) return;
      fetchPage(option.href).catch(() => {});
    }

    onVariantPickerInteraction(event) {
      const isLoading = this.hasAttribute('aria-busy');
      if ((!this.activeProductId && !isLoading) || !(event.target instanceof HTMLInputElement)) return;
      if (!event.target.closest('variant-picker')) return;
      // A different variant continues through the theme's variant picker (re-render + URL update).
      // Clicking the variant that was already selected only needs the host content back.
      this.showHost();
    }

    async showLinkedProduct(option) {
      const productId = option.dataset.productId;
      if (productId === this.activeProductId) return;

      const url = option.href;
      const requestId = ++this.requestId;
      this.setLoading(true);

      try {
        const html = await fetchPage(url);
        if (requestId !== this.requestId) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        assignHostPdpHandles(doc, true);
        const linkedSection = doc.querySelector('.section-main-product');
        if (!linkedSection?.querySelector('[data-variant-info="variant_id"]')) {
          throw new Error(`No product form found in ${url}`);
        }

        this.restoreHost();
        this.applyLinkedProduct(doc, linkedSection);
        this.showStyleDescription(productId);
        this.activeProductId = productId;

        document.title = doc.title || this.hostTitle;
        this.select(productId);
        this.section.dispatchEvent(
          new CustomEvent('linked-product:change', { bubbles: true, detail: { productId, url } })
        );
      } catch (error) {
        if (requestId !== this.requestId) return;
        console.error('Linked products group: falling back to navigation', error);
        window.location.href = url;
      } finally {
        if (requestId === this.requestId) this.setLoading(false);
      }
    }

    showHost() {
      // Cancels a linked product that is still loading.
      this.requestId++;
      this.setLoading(false);
      if (!this.activeProductId) return;

      this.restoreHost();
      document.title = this.hostTitle;

      this.select(null);
      this.section.dispatchEvent(
        new CustomEvent('linked-product:change', {
          bubbles: true,
          detail: { productId: this.hostProductId, url: window.location.href },
        })
      );
    }

    applyLinkedProduct(doc, linkedSection) {
      const section = this.section;

      // Host variant picker: no option shown as selected (styles in the snippet)
      section.classList.add('is-linked-product-active');
      this.undoStack.push(() => section.classList.remove('is-linked-product-active'));

      // Gallery
      this.swap(findByHandle(section, 'product-media'), findByHandle(linkedSection, 'product-media'));

      // Price, SKU, variant id, add to cart button, installments...
      this.updateVariantInfo(linkedSection);

      // Style line-item property. The host form keeps its own fields and only copies
      // [data-variant-info], so properties[Style] from the linked product's PDP is left behind.
      this.syncStyleProperty(linkedSection);

      // Shop Pay installments: after updateVariantInfo, so the swapped widget reads the linked variant id
      this.swap(findByHandle(section, 'payment-terms'), findByHandle(linkedSection, 'payment-terms'));

      // Benefits list (app block). Hidden, not detached: see hideAndInsert.
      this.hideAndInsert(findByHandle(document, 'benefits-list'), findByHandle(doc, 'benefits-list'), (element) =>
        section.querySelector('.product-variant-picker')?.after(element)
      );

      // Safety standards badges
      this.swap(findByHandle(section, 'safety-standards'), findByHandle(linkedSection, 'safety-standards'), (element) =>
        section.querySelector('.product-name-wrapper')?.after(element)
      );

      // Buy with prescription button
      this.swap(findByHandle(section, 'rx-button'), findByHandle(linkedSection, 'rx-button'), (element) =>
        section.querySelector('.product-buy-buttons')?.after(element)
      );

      this.swap(findByHandle(section, 'customize-cta'), findByHandle(linkedSection, 'customize-cta'), (element) =>
        section.querySelector('.product-buy-buttons')?.after(element)
      );

      // Sticky add to cart
      this.swap(
        findByHandle(section, 'sticky-add-to-cart'),
        findByHandle(linkedSection, 'sticky-add-to-cart'),
        (element) => section.append(element)
      );

      // 30-day returns confidence icon
      const returnsConfidence = findByHandle(section, RETURNS_CONFIDENCE_HANDLE);
      if (returnsConfidence) this.hide(returnsConfidence);

      this.swapTemplateSections(doc);
    }

    // Copies custom.style_description into the host product form as properties[Style].
    // The linked product's own PDP renders this hidden input; the host form does not
    // when the host product has no style description, and updateVariantInfo never inserts it.
    syncStyleProperty(linkedSection) {
      const form = this.section.querySelector('product-form form');
      if (!form) return;

      const linkedInput = linkedSection.querySelector('product-form form input[name="properties[Style]"]');
      const hostInput = form.querySelector('input[name="properties[Style]"]');
      const linkedValue = linkedInput ? (linkedInput.value || linkedInput.getAttribute('value') || '').trim() : '';

      if (linkedValue) {
        if (hostInput) {
          const originalValue = hostInput.value;
          hostInput.value = linkedValue;
          this.undoStack.push(() => {
            hostInput.value = originalValue;
          });
        } else {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[Style]';
          input.value = linkedValue;
          const idInput = form.querySelector('input[name="id"]');
          if (idInput) idInput.after(input);
          else form.prepend(input);
          this.undoStack.push(() => input.remove());
        }
        return;
      }

      // The linked product has no style description. Drop the host value so it is not
      // submitted with the linked variant. Hiding the input would still include it in FormData.
      if (!hostInput || !hostInput.hasAttribute('name')) return;
      const name = hostInput.getAttribute('name');
      hostInput.removeAttribute('name');
      this.undoStack.push(() => hostInput.setAttribute('name', name));
    }

    updateVariantInfo(linkedSection) {
      const linkedInfo = new Map();
      linkedSection.querySelectorAll('[data-variant-info]').forEach((element) => {
        if (!linkedInfo.has(element.dataset.variantInfo)) linkedInfo.set(element.dataset.variantInfo, element);
      });

      this.section.querySelectorAll('.product-info [data-variant-info]').forEach((element) => {
        const key = element.dataset.variantInfo;
        if (HOST_ONLY_VARIANT_INFO.includes(key) || element.parentElement.closest('variant-picker')) return;

        const linkedElement = linkedInfo.get(key);
        if (!linkedElement) {
          this.hide(element);
          return;
        }

        const isField = element instanceof HTMLInputElement || element instanceof HTMLSelectElement;
        const original = { html: element.innerHTML, attributes: getAttributes(element), value: element.value };

        element.innerHTML = linkedElement.innerHTML;
        setAttributes(element, getAttributes(linkedElement));
        if (isField) {
          // product-form enables its [name=id] input on load; the fetched markup has it disabled.
          if (element.name === 'id') element.removeAttribute('disabled');
          // Lets listeners such as Shop Pay installments pick up the new variant, like the theme does.
          if (element.value !== original.value) element.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.undoStack.push(() => {
          element.innerHTML = original.html;
          setAttributes(element, original.attributes);
          if (isField && element.value !== linkedElement.value) {
            element.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    // Hides the handled host template sections and shows the linked product's right after the main product,
    // so they stay above the shared sections (recently viewed, apps, related products).
    swapTemplateSections(doc) {
      const selector = '.shopify-section[data-host-pdp-section-handle]';
      const hostSections = Array.from(document.querySelectorAll(selector));
      const linkedSections = Array.from(doc.querySelectorAll(selector));

      hostSections.forEach((section) => this.hide(section));
      if (!linkedSections.length) return;

      this.section.after(...linkedSections);
      linkedSections.forEach(runInlineScripts);
      this.undoStack.push(() => linkedSections.forEach((section) => section.remove()));
    }

    // Replaces hostElement with linkedElement. Hides hostElement when the linked product has no
    // equivalent, and inserts linkedElement with insert() when the host has none.
    swap(hostElement, linkedElement, insert) {
      if (hostElement && linkedElement) {
        pinSectionReferences(hostElement);
        hostElement.replaceWith(linkedElement);
        pinSectionReferences(linkedElement);
        this.undoStack.push(() => linkedElement.replaceWith(hostElement));
      } else if (hostElement) {
        this.hide(hostElement);
      } else if (linkedElement && insert) {
        insert(linkedElement);
        pinSectionReferences(linkedElement);
        this.undoStack.push(() => linkedElement.remove());
      }
    }

    /**
     * Like swap, but hides hostElement in place instead of detaching it, and inserts linkedElement right after it.
     * For elements whose connectedCallback is not safe to run twice: Section Store's benefits list removes its
     * empty items on connect, so re-attaching the host's list throws on the missing item.
     */
    hideAndInsert(hostElement, linkedElement, insert) {
      if (hostElement) this.hide(hostElement);
      if (!linkedElement) return;

      if (hostElement) {
        hostElement.after(linkedElement);
      } else if (insert) {
        insert(linkedElement);
      } else {
        return;
      }
      this.undoStack.push(() => linkedElement.remove());
    }

    hide(element) {
      const display = element.style.getPropertyValue('display');
      const priority = element.style.getPropertyPriority('display');
      element.style.setProperty('display', 'none', 'important');
      this.undoStack.push(() => {
        if (display) {
          element.style.setProperty('display', display, priority);
        } else {
          element.style.removeProperty('display');
        }
      });
    }

    restoreHost() {
      while (this.undoStack.length) this.undoStack.pop()();
      this.activeProductId = null;
    }

    // Highlights the linked product's thumbnail; null clears the selection (host showing).
    select(productId) {
      this.options.forEach((option) => {
        option.setAttribute('aria-current', String(option.dataset.productId === productId));
      });
    }

    // Shows the linked product's Style (selected options + short description). The host Style is
    // restored by the undo; after that the theme's variant picker keeps it in sync with host variants.
    showStyleDescription(productId) {
      const style = this.querySelector('[data-linked-products-style]');
      if (!style) return;

      const original = style.innerHTML;
      const template = this.querySelector(`template[data-linked-product-style="${productId}"]`);
      style.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      this.undoStack.push(() => {
        style.innerHTML = original;
      });
    }

    setLoading(loading) {
      if (loading) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
      this.section.classList.toggle('is-linked-product-loading', loading);
    }
  }

  customElements.define('linked-products-group', LinkedProductsGroup);
})();
